import { AudioController, AudioCue, DEFAULT_AUDIO_CUE } from '../types/audio';
import { DroneSynth } from './DroneSynth';
import { NoiseGen } from './NoiseGen';
import { SFXGen } from './SFXGen';

export interface AudioEngineState {
  isUnlocked: boolean;
  isMuted: boolean;
  volume: number;
  currentCue: AudioCue;
}

export type AudioStateListener = (state: AudioEngineState) => void;

/**
 * Pure procedural Web Audio synthesis engine for "The Caesura Engine".
 * Implements the AudioController interface with zero external audio assets (0 MP3/WAV files).
 */
export class AudioEngine implements AudioController {
  private ctx: AudioContext | null = null;
  private isAudioUnlocked = false;
  private muted = false;
  private masterVolume = 0.75; // [0.0, 1.0]

  private masterBusGain: GainNode | null = null;
  private caesuraFilterNode: BiquadFilterNode | null = null;
  private masterGainNode: GainNode | null = null;
  private compressorNode: DynamicsCompressorNode | null = null;

  private droneSynth: DroneSynth | null = null;
  private noiseGen: NoiseGen | null = null;
  private sfxGen: SFXGen | null = null;

  // Caesura void tinnitus tone
  private tinnitusOsc: OscillatorNode | null = null;
  private tinnitusGain: GainNode | null = null;

  // Real-time audio spectrum analyser node
  private analyserNode: AnalyserNode | null = null;

  private currentCue: AudioCue = { ...DEFAULT_AUDIO_CUE };
  private listeners: Set<AudioStateListener> = new Set();

  constructor(customCtx?: AudioContext) {
    if (customCtx) {
      this.initGraph(customCtx);
    }
  }

  /**
   * Initializes or returns the current AudioContext.
   */
  public getAudioContext(): AudioContext {
    if (!this.ctx) {
      const AudioCtxClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

      if (!AudioCtxClass) {
        throw new Error('Web Audio API is not supported in this environment.');
      }

      this.ctx = new AudioCtxClass();
      this.initGraph(this.ctx);
    }
    return this.ctx;
  }

  /**
   * Sets up the complete procedural audio node graph:
   * [DroneSynth] ──┐
   * [NoiseGen]   ──┼─► [MasterBus] ─► [CaesuraFilter] ─► [MasterGain] ─► [Compressor] ─► [Destination]
   * [SFXGen]     ──┘
   */
  private initGraph(ctx: AudioContext): void {
    this.ctx = ctx;

    // Master bus
    this.masterBusGain = ctx.createGain();
    this.masterBusGain.gain.setValueAtTime(0.9, ctx.currentTime);

    // "Caesura Pause" transition filter (low-pass filter with 20kHz ceiling)
    this.caesuraFilterNode = ctx.createBiquadFilter();
    this.caesuraFilterNode.type = 'lowpass';
    this.caesuraFilterNode.frequency.setValueAtTime(20000, ctx.currentTime);
    this.caesuraFilterNode.Q.setValueAtTime(1.0, ctx.currentTime);

    // Master volume gain node
    this.masterGainNode = ctx.createGain();
    const effectiveGain = this.calculateLogarithmicGain(this.masterVolume);
    this.masterGainNode.gain.setValueAtTime(this.muted ? 0 : effectiveGain, ctx.currentTime);

    // Dynamics compressor for speaker protection and warm glue
    this.compressorNode = ctx.createDynamicsCompressor();
    this.compressorNode.threshold.setValueAtTime(-18, ctx.currentTime);
    this.compressorNode.knee.setValueAtTime(12, ctx.currentTime);
    this.compressorNode.ratio.setValueAtTime(4, ctx.currentTime);
    this.compressorNode.attack.setValueAtTime(0.003, ctx.currentTime);
    this.compressorNode.release.setValueAtTime(0.25, ctx.currentTime);

    // Connect master chain
    this.masterBusGain.connect(this.caesuraFilterNode);
    this.caesuraFilterNode.connect(this.masterGainNode);
    this.masterGainNode.connect(this.compressorNode);
    this.compressorNode.connect(ctx.destination);

    // Initialize procedural generators
    this.droneSynth = new DroneSynth(ctx);
    this.noiseGen = new NoiseGen(ctx);
    this.sfxGen = new SFXGen(ctx);

    this.droneSynth.connect(this.masterBusGain);
    this.noiseGen.connect(this.masterBusGain);
    this.sfxGen.connect(this.masterBusGain);

    // Real-time FFT spectrum analyser (64 bins for responsive 32-bar visualizer)
    try {
      this.analyserNode = ctx.createAnalyser();
      this.analyserNode.fftSize = 64;
      this.analyserNode.smoothingTimeConstant = 0.8;
      this.masterBusGain.connect(this.analyserNode);
    } catch {
      // Analyser fallback
    }

    if (ctx.state === 'running') {
      this.isAudioUnlocked = true;
      this.startSynths();
    }
  }

  private startSynths(): void {
    this.droneSynth?.start();
    this.noiseGen?.start();
    this.sfxGen?.start();
    this.updateAudioCue(this.currentCue);
  }

  /**
   * Logarithmic volume scaling.
   * Transforms linear slider [0.0, 1.0] to a perceptual acoustic curve (approx. 40dB range).
   */
  public calculateLogarithmicGain(val: number): number {
    const clamped = Math.max(0.0, Math.min(1.0, val));
    if (clamped <= 0.0) return 0.0;
    // Standard perceptual exponential curve: (10^(clamped * 2) - 1) / 99
    return (Math.pow(10, clamped * 2) - 1) / 99;
  }

  /**
   * User gesture unlock complying with browser autoplay restrictions.
   */
  public async unlockAudio(): Promise<boolean> {
    try {
      const ctx = this.getAudioContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      this.isAudioUnlocked = true;
      this.startSynths();
      this.notifyListeners();
      return true;
    } catch (err) {
      console.warn('[AudioEngine] Audio unlock failed:', err);
      return false;
    }
  }

  /**
   * Smoothly crossfades audio cues over 2.0 seconds.
   * Implements the "Caesura Pause" filter sweep on caesura_silence.
   */
  public updateAudioCue(cue: AudioCue): void {
    this.currentCue = { ...cue };
    if (!this.ctx || !this.caesuraFilterNode) {
      return;
    }

    const t = this.ctx.currentTime;
    const duration = 2.0;

    if (cue.mood === 'caesura_silence') {
      // "Caesura Pause" transition filter sweep:
      // Sudden eerie dead silence dropping filter cutoff down to 0Hz in 0.4s
      this.caesuraFilterNode.frequency.cancelScheduledValues(t);
      this.caesuraFilterNode.frequency.setValueAtTime(this.caesuraFilterNode.frequency.value, t);
      this.caesuraFilterNode.frequency.linearRampToValueAtTime(0, t + 0.4);

      // Trigger faint high-Q unrendered Planck gap tinnitus ring (3840Hz)
      this.triggerVoidTinnitus(t);
    } else {
      // Re-open master filter smoothly over 2.0s
      this.caesuraFilterNode.frequency.cancelScheduledValues(t);
      this.caesuraFilterNode.frequency.setValueAtTime(this.caesuraFilterNode.frequency.value, t);
      this.caesuraFilterNode.frequency.linearRampToValueAtTime(20000, t + duration);
    }

    // Update child modules
    this.droneSynth?.updateCue(cue, duration);
    this.noiseGen?.updateCue(cue, duration);
    this.sfxGen?.updateCue(cue, duration);

    this.notifyListeners();
  }

  private triggerVoidTinnitus(t: number): void {
    if (!this.ctx || !this.masterGainNode) return;

    try {
      this.tinnitusOsc?.stop();
      this.tinnitusOsc?.disconnect();
    } catch {
      // Ignore
    }

    try {
      this.tinnitusOsc = this.ctx.createOscillator();
      this.tinnitusGain = this.ctx.createGain();

      this.tinnitusOsc.type = 'sine';
      this.tinnitusOsc.frequency.setValueAtTime(3840, t);

      // Faint high-pitched ring decaying into the stillness
      this.tinnitusGain.gain.setValueAtTime(0.0001, t);
      this.tinnitusGain.gain.linearRampToValueAtTime(0.015, t + 0.1);
      this.tinnitusGain.gain.exponentialRampToValueAtTime(0.0001, t + 3.2);

      this.tinnitusOsc.connect(this.tinnitusGain);
      this.tinnitusGain.connect(this.masterGainNode);

      this.tinnitusOsc.start(t);
      this.tinnitusOsc.stop(t + 3.3);
    } catch {
      // Audio node allocation error safeguard
    }
  }

  /**
   * Tactile page-turn sound trigger without loading audio files.
   */
  public triggerPageTurnSound(): void {
    if (!this.isAudioUnlocked || this.muted) return;
    this.sfxGen?.triggerPageTurnSound(0.12 * Math.min(this.masterVolume, 1.0));
  }

  /**
   * Sets master volume with logarithmic scaling and anti-click ramp.
   */
  public setMasterVolume(val: number): void {
    this.masterVolume = Math.max(0.0, Math.min(1.0, val));

    if (!this.ctx || !this.masterGainNode) {
      this.notifyListeners();
      return;
    }

    const t = this.ctx.currentTime;
    const targetGain = this.muted ? 0.0 : this.calculateLogarithmicGain(this.masterVolume);

    this.masterGainNode.gain.cancelScheduledValues(t);
    this.masterGainNode.gain.setValueAtTime(this.masterGainNode.gain.value, t);
    this.masterGainNode.gain.linearRampToValueAtTime(targetGain, t + 0.035);

    this.notifyListeners();
  }

  /**
   * Toggles mute state with pop-free linear gain ramping.
   */
  public toggleMute(): boolean {
    this.muted = !this.muted;

    if (this.ctx && this.masterGainNode) {
      const t = this.ctx.currentTime;
      const targetGain = this.muted ? 0.0 : this.calculateLogarithmicGain(this.masterVolume);

      // Pop-free linear gain ramping over 40ms
      this.masterGainNode.gain.cancelScheduledValues(t);
      this.masterGainNode.gain.setValueAtTime(this.masterGainNode.gain.value, t);
      this.masterGainNode.gain.linearRampToValueAtTime(targetGain, t + 0.04);
    }

    this.notifyListeners();
    return this.muted;
  }

  public isMuted(): boolean {
    return this.muted;
  }

  public isUnlocked(): boolean {
    return this.isAudioUnlocked;
  }

  public getVolume(): number {
    return this.masterVolume;
  }

  public getCurrentCue(): AudioCue {
    return { ...this.currentCue };
  }

  public getState(): AudioEngineState {
    return {
      isUnlocked: this.isAudioUnlocked,
      isMuted: this.muted,
      volume: this.masterVolume,
      currentCue: { ...this.currentCue },
    };
  }

  // Observable subscription for React UI controls
  public onStateChange(listener: AudioStateListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (err) {
        console.error('[AudioEngine] Listener error:', err);
      }
    });
  }

  // Introspection methods for unit testing
  public getMasterGainNode(): GainNode | null {
    return this.masterGainNode;
  }

  public getCaesuraFilterNode(): BiquadFilterNode | null {
    return this.caesuraFilterNode;
  }

  public getDroneSynth(): DroneSynth | null {
    return this.droneSynth;
  }

  public getNoiseGen(): NoiseGen | null {
    return this.noiseGen;
  }

  public getSFXGen(): SFXGen | null {
    return this.sfxGen;
  }

  public getMasterBus(): GainNode | null {
    return this.masterBusGain;
  }

  public getAnalyserNode(): AnalyserNode | null {
    return this.analyserNode;
  }

  /**
   * Adjusts the dark ambient sound layers: deep drone, sub-bass rumble, and cosmic void noise.
   */
  public setDarkAtmosphere(params: {
    darkDroneBoost?: number;
    subBassRumble?: number;
    cosmicHiss?: number;
  }): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;

    if (params.darkDroneBoost !== undefined && this.droneSynth) {
      const droneGain = this.droneSynth.getDroneGainNode();
      const target = Math.max(0.05, Math.min(1.0, 0.25 + params.darkDroneBoost * 0.6));
      droneGain.gain.cancelScheduledValues(t);
      droneGain.gain.linearRampToValueAtTime(target, t + 0.3);
    }

    if (params.subBassRumble !== undefined && this.sfxGen) {
      const subGain = this.sfxGen.getSubharmonicGainNode();
      const target = Math.max(0.05, Math.min(1.0, 0.15 + params.subBassRumble * 0.7));
      subGain.gain.cancelScheduledValues(t);
      subGain.gain.linearRampToValueAtTime(target, t + 0.3);
    }

    if (params.cosmicHiss !== undefined && this.noiseGen) {
      const noiseGain = this.noiseGen.getNoiseGainNode();
      const target = Math.max(0.01, Math.min(0.6, 0.04 + params.cosmicHiss * 0.4));
      noiseGain.gain.cancelScheduledValues(t);
      noiseGain.gain.linearRampToValueAtTime(target, t + 0.3);
    }
  }

  /**
   * Applies themed dark ambient atmosphere presets.
   */
  public applyAtmospherePreset(
    preset: 'geryon_keel' | 'deep_margin' | 'planck_ignition' | 'obsidian_ledger' | 'caesura_silence'
  ): void {
    switch (preset) {
      case 'geryon_keel':
        this.updateAudioCue({
          mood: 'subharmonic_pulse',
          intensity: 0.85,
          subharmonicBass: 0.95,
          dissonance: 0.2,
          glitchFrequency: 0.05,
        });
        this.setDarkAtmosphere({ darkDroneBoost: 0.7, subBassRumble: 0.9, cosmicHiss: 0.25 });
        break;
      case 'deep_margin':
        this.updateAudioCue({
          mood: 'deep_drone',
          intensity: 0.9,
          subharmonicBass: 0.7,
          dissonance: 0.65,
          glitchFrequency: 0.1,
        });
        this.setDarkAtmosphere({ darkDroneBoost: 0.85, subBassRumble: 0.6, cosmicHiss: 0.8 });
        break;
      case 'planck_ignition':
        this.updateAudioCue({
          mood: 'quantum_flutter',
          intensity: 0.95,
          subharmonicBass: 0.6,
          dissonance: 0.85,
          glitchFrequency: 0.25,
        });
        this.setDarkAtmosphere({ darkDroneBoost: 0.9, subBassRumble: 0.75, cosmicHiss: 0.7 });
        break;
      case 'obsidian_ledger':
        this.updateAudioCue({
          mood: 'deep_drone',
          intensity: 0.95,
          subharmonicBass: 1.0,
          dissonance: 0.5,
          glitchFrequency: 0.05,
        });
        this.setDarkAtmosphere({ darkDroneBoost: 0.95, subBassRumble: 1.0, cosmicHiss: 0.4 });
        break;
      case 'caesura_silence':
        this.updateAudioCue({
          mood: 'caesura_silence',
          intensity: 0.0,
          subharmonicBass: 0.0,
          dissonance: 0.0,
          glitchFrequency: 0.0,
        });
        break;
    }
  }
}

// Global Singleton Instance
let globalAudioEngine: AudioEngine | null = null;

export function getAudioEngine(): AudioEngine {
  if (!globalAudioEngine) {
    globalAudioEngine = new AudioEngine();
  }
  return globalAudioEngine;
}

export const audioEngine: AudioController = getAudioEngine();
